import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { useAuth } from '../contexts/AuthContext'
import { useDocument, useCollection } from '../hooks/useFirestore'
import { formatDate, parseDate } from '../lib/harptos'
import { getActiveImage } from '../lib/characterUtils'
import RelationshipWeb from '../components/characters/RelationshipWeb'
import CharacterNotes from '../components/characters/CharacterNotes'
import ImageLightbox from '../components/common/ImageLightbox'
import CharacterSpellbook from '../components/spells/CharacterSpellbook'
import './CharacterDetailPage.css'

// Placeholder avatar
const PLACEHOLDER_AVATAR = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <circle cx="50" cy="35" r="20" fill="#6E2EBF"/>
    <ellipse cx="50" cy="85" rx="30" ry="25" fill="#6E2EBF"/>
  </svg>
`)

// Sanitize HTML content
function sanitizeHtml(html) {
  if (!html) return ''
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'blockquote'],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  })
}

// Generate inline styles for crop positioning
function getCropStyle(crop) {
  if (!crop) return {}

  // x and y are the center point of the crop as percentages (0-100)
  // Use them directly for object-position
  if (crop.x !== undefined && crop.y !== undefined) {
    // Validate values are in reasonable range (0-100)
    const x = Math.max(0, Math.min(100, crop.x))
    const y = Math.max(0, Math.min(100, crop.y))
    return { objectPosition: `${x}% ${y}%` }
  }

  return {}
}

export default function CharacterDetailPage() {
  const { id } = useParams()
  const { isOwner } = useAuth()
  const { data: character, loading, error } = useDocument('characters', id)
  const { data: allCharacters } = useCollection('characters')
  const { data: allEvents } = useCollection('events')
  const { data: allHandouts } = useCollection('handouts')
  const [showLightbox, setShowLightbox] = useState(false)

  // Filter events that include this character
  const linkedEvents = allEvents.filter(event =>
    event.characterIds?.includes(id)
  ).sort((a, b) => {
    const aYear = a.start?.year || 0
    const bYear = b.start?.year || 0
    if (aYear !== bYear) return aYear - bYear
    const aMonth = a.start?.month || ''
    const bMonth = b.start?.month || ''
    return aMonth.localeCompare(bMonth)
  })

  // Filter handouts that link to this character (and are not hidden for non-owners)
  const linkedHandouts = allHandouts.filter(handout => {
    if (!handout.characterIds?.includes(id)) return false
    if (handout.hidden === true && !isOwner) return false
    return true
  }).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))

  if (loading) {
    return (
      <div className="container">
        <div className="character-detail-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  if (error || !character) {
    return (
      <div className="container">
        <div className="character-detail-error">
          <h2>Character not found</h2>
          <Link to="/characters" className="btn-secondary">Back to Characters</Link>
        </div>
      </div>
    )
  }

  // Hide character from non-owners if hidden flag is set
  if (character.hidden === true && !isOwner) {
    return (
      <div className="container">
        <div className="character-detail-error">
          <h2>Character not found</h2>
          <Link to="/characters" className="btn-secondary">Back to Characters</Link>
        </div>
      </div>
    )
  }

  const {
    name,
    class: charClass,
    race,
    gender,
    title,
    age,
    alignment,
    height,
    weight,
    hair,
    eyes,
    skin,
    faith,
    faction,
    dndBeyondUrl,
    lore,
    type,
    active,
    deceased,
    linkedUserId
  } = character

  // Get active image (true form if revealed, otherwise profile)
  const activeImage = getActiveImage(character)
  const cropStyle = getCropStyle(activeImage.crop)

  const isInactive = active === false
  const isDeceased = deceased === true

  return (
    <div className="container">
      <Link to="/characters" className="back-link">← Back to Characters</Link>

      <div className={`character-detail ${isInactive ? 'inactive' : ''} ${isDeceased ? 'deceased' : ''}`}>
        <aside className="character-sidebar">
          <div
            className={`character-portrait ${activeImage.url ? 'clickable' : ''}`}
            onClick={() => activeImage.url && setShowLightbox(true)}
            role={activeImage.url ? 'button' : undefined}
            tabIndex={activeImage.url ? 0 : undefined}
            onKeyDown={(e) => activeImage.url && e.key === 'Enter' && setShowLightbox(true)}
          >
            <img
              src={activeImage.url || PLACEHOLDER_AVATAR}
              alt={name}
              style={cropStyle}
              onError={(e) => { e.target.src = PLACEHOLDER_AVATAR }}
            />
            {isDeceased && (
              <span className="character-deceased-badge">Deceased</span>
            )}
            {isInactive && !isDeceased && (
              <span className="character-inactive-badge">Inactive</span>
            )}
            <span className={`character-type-badge ${type}`}>
              {type === 'pc' ? 'Party Member' : 'NPC'}
            </span>
          </div>

          <div className="character-stats">
            {charClass && (
              <div className="stat-row">
                <span className="stat-label">Class</span>
                <span className="stat-value">{charClass}</span>
              </div>
            )}
            {race && (
              <div className="stat-row">
                <span className="stat-label">Race</span>
                <span className="stat-value">{race}</span>
              </div>
            )}
            {gender && (
              <div className="stat-row">
                <span className="stat-label">Gender</span>
                <span className="stat-value">{gender.charAt(0).toUpperCase() + gender.slice(1)}</span>
              </div>
            )}
            {age && (
              <div className="stat-row">
                <span className="stat-label">Age</span>
                <span className="stat-value">{age}</span>
              </div>
            )}
            {alignment && (
              <div className="stat-row">
                <span className="stat-label">Alignment</span>
                <span className="stat-value">{alignment}</span>
              </div>
            )}
            {height && (
              <div className="stat-row">
                <span className="stat-label">Height</span>
                <span className="stat-value">{height}</span>
              </div>
            )}
            {weight && (
              <div className="stat-row">
                <span className="stat-label">Weight</span>
                <span className="stat-value">{weight}</span>
              </div>
            )}
            {hair && (
              <div className="stat-row">
                <span className="stat-label">Hair</span>
                <span className="stat-value">{hair}</span>
              </div>
            )}
            {eyes && (
              <div className="stat-row">
                <span className="stat-label">Eyes</span>
                <span className="stat-value">{eyes}</span>
              </div>
            )}
            {skin && (
              <div className="stat-row">
                <span className="stat-label">Skin</span>
                <span className="stat-value">{skin}</span>
              </div>
            )}
            {faith && (
              <div className="stat-row">
                <span className="stat-label">Faith</span>
                <span className="stat-value">{faith}</span>
              </div>
            )}
            {faction && (
              <div className="stat-row">
                <span className="stat-label">Faction</span>
                <span className="stat-value">{faction}</span>
              </div>
            )}
          </div>

          {dndBeyondUrl && (
            <a
              href={dndBeyondUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="dndbeyond-link"
            >
              View on D&D Beyond →
            </a>
          )}
        </aside>

        <main className="character-main">
          <header className="character-header">
            <h1>{name}</h1>
            {title && <p className="character-title">{title}</p>}
          </header>

          {lore && (
            <section className="character-lore">
              <h2>Lore</h2>
              <div
                className="lore-content"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(lore) }}
              />
            </section>
          )}

          {character.relationships?.length > 0 && (
            isOwner || character.relationships.some(r => !r.hidden)
          ) && (
            <section className="character-web">
              <h2>Web</h2>
              <RelationshipWeb
                relationships={character.relationships}
                allCharacters={allCharacters}
                showHidden={isOwner}
              />
            </section>
          )}

          {linkedEvents.length > 0 && (
            <section className="character-events">
              <h2>Timeline Events</h2>
              <div className="events-list">
                {linkedEvents.map(event => (
                  <div key={event.id} className={`event-item category-${event.category}`}>
                    <div className="event-date">
                      {formatDate(parseDate(event.start))}
                    </div>
                    <div className="event-content">
                      <h4>{event.title}</h4>
                      {event.location && (
                        <span className="event-location">{event.location}</span>
                      )}
                      {event.description && (
                        <p className="event-description">{event.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {linkedHandouts.length > 0 && (
            <section className="character-handouts">
              <h2>Documents</h2>
              <div className="handouts-list">
                {linkedHandouts.map(handout => (
                  <a
                    key={handout.id}
                    href={handout.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="handout-item"
                  >
                    {handout.thumbnailUrl ? (
                      <img
                        src={handout.thumbnailUrl}
                        alt=""
                        className="handout-thumbnail"
                      />
                    ) : (
                      <span className="handout-icon">📜</span>
                    )}
                    <div className="handout-info">
                      <h4>{handout.title}</h4>
                      {handout.description && (
                        <p>{handout.description}</p>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          <CharacterSpellbook characterId={id} linkedUserId={linkedUserId} />

          <CharacterNotes characterId={id} linkedUserId={linkedUserId} />

          {!lore && linkedEvents.length === 0 && linkedHandouts.length === 0 && (
            <div className="character-empty">
              <p>No lore, events, or documents yet for this character.</p>
            </div>
          )}
        </main>
      </div>

      {showLightbox && activeImage.url && (
        <ImageLightbox
          imageUrl={activeImage.url}
          alt={name}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </div>
  )
}
